import React from 'react';
import { GradientText } from '../components/ui/GradientText';
import { Code2, Users, Rocket, Heart, Target, Coffee, Zap, ArrowRight, BrainCircuit, Briefcase } from 'lucide-react';
import Footer from '../components/Footer';

export default function Careers() {
  return (
    <div className="bg-black">
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,70,229,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(219,39,119,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Rejoignez l'Aventure <GradientText>Botmaker</GradientText>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Participez à la révolution de l'IA conversationnelle et construisez avec nous le futur du service client.
            </p>
          </div>
        </div>
      </div>

      {/* Notre Culture */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Notre <GradientText>Culture</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Chez Botmaker, nous cultivons un environnement de travail stimulant où l'innovation et le bien-être sont au cœur de nos priorités.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Rocket className="w-12 h-12 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Innovation</h3>
              <p className="text-gray-400">Nous encourageons la créativité et l'expérimentation pour repousser les limites de la technologie.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Heart className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Bien-être</h3>
              <p className="text-gray-400">Équilibre vie pro/perso, télétravail flexible et avantages sociaux pour votre épanouissement.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Target className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Impact</h3>
              <p className="text-gray-400">Vos idées et votre travail ont un impact direct sur des milliers d'utilisateurs.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Users className="w-12 h-12 text-green-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Collaboration</h3>
              <p className="text-gray-400">Une équipe soudée et passionnée qui travaille main dans la main vers un objectif commun.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Coffee className="w-12 h-12 text-yellow-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Convivialité</h3>
              <p className="text-gray-400">Des événements d'équipe réguliers et une ambiance de travail décontractée.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Zap className="w-12 h-12 text-orange-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Formation</h3>
              <p className="text-gray-400">Budget formation personnel et accompagnement dans votre développement professionnel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Offres d'emploi */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Nos <GradientText>Offres</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Découvrez nos opportunités et rejoignez une équipe passionnée par l'innovation.
            </p>
          </div>

          <div className="space-y-8">
            {/* Développeur Full Stack */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center shrink-0">
                  <BrainCircuit className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="text-xl font-semibold text-white">Développeur Full Stack</h3>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-500/20 text-indigo-400">
                      Full Time
                    </span>
                  </div>
                  <p className="text-gray-300 mb-6">
                    Nous recherchons un développeur Full Stack expérimenté pour rejoindre notre équipe technique. Vous participerez au développement de nouvelles fonctionnalités et à l'amélioration de notre plateforme de chatbot IA.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-white font-medium mb-2">Responsabilités :</h4>
                      <ul className="list-disc pl-6 text-gray-400 space-y-1">
                        <li>Développement de nouvelles fonctionnalités</li>
                        <li>Optimisation des performances</li>
                        <li>Code review et mentoring</li>
                        <li>Participation aux choix d'architecture</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-2">Profil recherché :</h4>
                      <ul className="list-disc pl-6 text-gray-400 space-y-1">
                        <li>5+ ans d'expérience en développement web</li>
                        <li>Maîtrise de React, Node.js, TypeScript</li>
                        <li>Expérience avec les architectures cloud</li>
                        <li>Intérêt pour l'IA et le NLP</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8">
                    <a 
                      href="mailto:careers@botmaker-agency.com?subject=Candidature%20Développeur%20Full%20Stack"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 text-white rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
                    >
                      Postuler
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Commercial Senior */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center shrink-0">
                  <Briefcase className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="text-xl font-semibold text-white">Commercial Senior</h3>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-400">
                      Full Time
                    </span>
                  </div>
                  <p className="text-gray-300 mb-6">
                    Nous recherchons un Commercial Senior pour développer notre portefeuille clients et gérer les comptes stratégiques. Vous serez responsable de la croissance de notre activité sur le marché français.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-white font-medium mb-2">Responsabilités :</h4>
                      <ul className="list-disc pl-6 text-gray-400 space-y-1">
                        <li>Prospection et acquisition de nouveaux clients</li>
                        <li>Gestion et développement du portefeuille existant</li>
                        <li>Négociation de contrats complexes</li>
                        <li>Reporting et suivi des KPIs commerciaux</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-2">Profil recherché :</h4>
                      <ul className="list-disc pl-6 text-gray-400 space-y-1">
                        <li>5+ ans d'expérience en vente B2B</li>
                        <li>Expérience dans la vente de solutions SaaS</li>
                        <li>Excellent relationnel et capacité de négociation</li>
                        <li>Autonomie et esprit d'initiative</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8">
                    <a 
                      href="mailto:careers@botmaker-agency.com?subject=Candidature%20Commercial%20Senior"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 text-white rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
                    >
                      Postuler
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Candidature Spontanée */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl p-1">
            <div className="bg-black rounded-xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Pas de poste qui vous correspond ?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Nous sommes toujours à la recherche de talents passionnés. Envoyez-nous votre candidature spontanée et nous vous recontacterons dès qu'une opportunité correspondant à votre profil se présentera.
              </p>
              <a 
                href="mailto:careers@botmaker-agency.com?subject=Candidature%20Spontanée"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 text-white rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
              >
                Envoyer ma candidature
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}